import { AutoAwesome } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { StructureSet } from '~/domain/patient/structureset';
import useAvailableStructureSets from '~/hooks/structure-set/useAvailableStructureSets';
import { useSegmentationMetrics } from '~/hooks/workflows/backend/tasks/useSegmentationMetrics';
import { environmentState } from '~/state/atoms/engineAtoms';
import { cutUID } from '~/utils/string';
import SegmetricsTable from './SegmetricsTable';

export default function Segmetrics() {
    const [testStructureSet, setTestStructureSet] = useState<StructureSet>();
    const [referenceStructureSet, setReferenceStructureSet] =
        useState<StructureSet>();
    const availableStructureSets = useAvailableStructureSets(true);
    const { submit, loading, data } = useSegmentationMetrics();
    const environment = useRecoilValue(environmentState);

    const handleSelectTestStructureSet = (event: SelectChangeEvent) => {
        const selectedStructureSet = availableStructureSets.find(
            (structureSet) => structureSet.sopInstanceUID === event.target.value
        );
        setTestStructureSet(selectedStructureSet?.structureSetObject);
        if (selectedStructureSet === referenceStructureSet) {
            setReferenceStructureSet(testStructureSet);
        }
    };

    const handleSelectReferenceStructure = (event: SelectChangeEvent) => {
        const selectedStructureSet = availableStructureSets.find(
            (structureSet) => structureSet.sopInstanceUID === event.target.value
        );
        setReferenceStructureSet(selectedStructureSet?.structureSetObject);
        if (selectedStructureSet === testStructureSet) {
            setTestStructureSet(referenceStructureSet);
        }
    };

    useEffect(() => {
        if (environment) {
            environment.saveChanges();
        }
    }, []);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="runStructureComparison" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="runStructureComparisonDescription" />
                </Typography>
            </Box>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    color: 'warning.main',
                    border: '1px solid',
                    borderColor: 'warning.main',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderRadius: `${theme.shape.borderRadius}px`,
                })}
            >
                <TriangleAlert
                    fontSize="24px"
                    size={32}
                    style={{ marginTop: -6 }}
                />
                <Typography variant="body2" color="inherit">
                    Structure names must be identical between the test and
                    reference sets to get comparison results. You can rename
                    structures in the workspace if needed.
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
                <FormControl fullWidth>
                    <InputLabel
                        id="test-structure-set-select-label"
                        size="small"
                        disabled={loading}
                    >
                        Test Structure Set
                    </InputLabel>
                    <Select
                        labelId="test-structure-set-select-label"
                        id="test-structure"
                        value={testStructureSet?.dicomUid ?? ''}
                        label={'Test Structure Set'}
                        onChange={handleSelectTestStructureSet}
                        size="small"
                        disabled={loading}
                    >
                        {availableStructureSets.map((structureSet) => (
                            <MenuItem
                                key={structureSet.sopInstanceUID}
                                value={structureSet.sopInstanceUID}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Typography fontSize={16}>
                                        {structureSet.label}
                                    </Typography>
                                    <Typography fontSize={14} variant="body2">
                                        {cutUID(structureSet.sopInstanceUID)}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel
                        id="reference-structure-set-select-label"
                        size="small"
                        disabled={loading}
                    >
                        Reference Structure Set
                    </InputLabel>
                    <Select
                        labelId="reference-structure-set-select-label"
                        id="reference-structure"
                        value={referenceStructureSet?.dicomUid ?? ''}
                        label={'Reference Structure Set'}
                        onChange={handleSelectReferenceStructure}
                        size="small"
                        disabled={loading}
                    >
                        {availableStructureSets.map((structureSet) => (
                            <MenuItem
                                key={structureSet.sopInstanceUID}
                                value={structureSet.sopInstanceUID}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Typography fontSize={16}>
                                        {structureSet.label}
                                    </Typography>
                                    <Typography fontSize={14} variant="body2">
                                        {cutUID(structureSet.sopInstanceUID)}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {data && <SegmetricsTable data={data} />}

            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
            >
                <Stack gap={0.5}>
                    <PseudonymizeCheckbox disabled={loading} />
                    <Button
                        onClick={() => {
                            if (testStructureSet && referenceStructureSet)
                                submit(testStructureSet, referenceStructureSet);
                        }}
                        disabled={
                            loading ||
                            !testStructureSet ||
                            !referenceStructureSet
                        }
                        variant="outlined"
                        endIcon={<AutoAwesome />}
                        sx={{ width: 160 }}
                        startIcon={
                            loading && (
                                <CircularProgress size={12} color="inherit" />
                            )
                        }
                    >
                        {loading ? (
                            <FormattedMessage id="loading" />
                        ) : (
                            <FormattedMessage id="start" />
                        )}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
